/**
 * Goals and memory data service
 * Handles assessment goals and patient memory (for RAG later)
 */

import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit as fsLimit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";

/**
 * Create goals for a question
 * @param {string} questionId
 * @param {Array} goalTexts
 */
export const createGoals = async (questionId, goalTexts) => {
  await Promise.all(
    goalTexts.map((text, index) =>
      setDoc(doc(db, "goals", `g-${questionId}-${index}`), {
        questionId,
        text,
        status: index === 0 ? "active" : "pending",
        insights: "",
        index,
        createdAt: serverTimestamp(),
      })
    )
  );
};

/**
 * Get goals for a question
 * @param {string} questionId
 * @returns {Array}
 */
export const getGoals = async (questionId) => {
  const qRef = query(collection(db, "goals"), where("questionId", "==", questionId));
  const snapshot = await getDocs(qRef);
  return snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.index || 0) - (b.index || 0));
};

/**
 * Update goal
 * @param {string} questionId
 * @param {number} goalIndex
 * @param {Object} updates
 */
export const updateGoal = async (questionId, goalIndex, updates) => {
  const goalId = `g-${questionId}-${goalIndex}`;
  await updateDoc(doc(db, "goals", goalId), updates);
};

/**
 * Add to patient memory (for RAG context later)
 * @param {string} patientId
 * @param {Object} memoryEntry - { date, summary, topics?, embeddings? }
 */
export const addPatientMemory = async (patientId, memoryEntry) => {
  await addDoc(collection(db, "patientMemory"), {
    patientId,
    date: memoryEntry.date,
    summary: memoryEntry.summary,
    topics: memoryEntry.topics || [],
    embeddings: memoryEntry.embeddings || null,
    createdAt: serverTimestamp(),
  });
};

/**
 * Get patient memory (for RAG context)
 * @param {string} patientId
 * @param {number} limit
 * @returns {Array}
 */
export const getPatientMemory = async (patientId, limit = 10) => {
  const qRef = query(
    collection(db, "patientMemory"),
    where("patientId", "==", patientId),
    fsLimit(limit)
  );
  const snapshot = await getDocs(qRef);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const subscribeGoals = (questionId, onData, onError) => {
  const qRef = query(collection(db, "goals"), where("questionId", "==", questionId));

  return onSnapshot(
    qRef,
    (snapshot) => {
      const goals = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.index || 0) - (b.index || 0));
      onData(goals);
    },
    (error) => {
      if (onError) onError(error);
    }
  );
};
